"use client";

import { ArrowRight, CircleCheck as CheckCircle2, Terminal, Code as Code2, Network, FileSearch } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: detailsRef,
    isVisible: detailsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const coreFocusAreas = [{
    title: "Crawlability & Site Architecture",
    description: "Eliminating broken crawl paths, orphan pages, and inefficient URL hierarchies so search bots navigate your structure without friction.",
    icon: Network
  }, {
    title: "Indexation & Cannibalisation",
    description: "Resolving index bloat, parameter duplication, and canonical conflicts to ensure only high-value, intent-matched URLs rank.",
    icon: FileSearch
  }, {
    title: "Rendering & JavaScript SEO",
    description: "Diagnosing client-side hydration gaps, DOM rendering bottlenecks, and critical resource delivery across modern web frameworks.",
    icon: Code2
  }];
  return <section data-section-id="2779" id="servicesgrid" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-6 md:px-10 lg:px-16 border-b border-border">
      {/* Subtle structural grid line background */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.05]" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 1200 1200" aria-hidden="true">
        <path d="M 100 200 L 400 200 L 400 450 L 800 450 L 800 700 L 1100 700" stroke="hsl(220 80% 30%)" strokeWidth="1" fill="none" />
        <path d="M 200 900 L 600 900 L 600 600 L 1000 600" stroke="hsl(220 80% 30%)" strokeWidth="1" fill="none" />
        <circle cx="400" cy="200" r="4" fill="hsl(220 80% 30%)" />
        <circle cx="800" cy="450" r="4" fill="hsl(220 80% 30%)" />
        <circle cx="600" cy="900" r="4" fill="hsl(220 80% 30%)" />
      </svg>

      <div className="relative max-w-7xl mx-auto">
        {/* Main Hero Header and First-Person Introduction */}
        <div ref={heroRef} className={`max-w-4xl transition-all duration-700 ease-out ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground text-xs font-mono tracking-wider uppercase mb-6">
            <Terminal className="size-3.5 text-primary" />
            <span>Consultant Specialism · Architecture &amp; Crawlability</span>
          </div>

          <h1 className="font-serif font-default text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.08] mb-8">
            Technical SEO
          </h1>

          <div className="space-y-5 text-base sm:text-lg text-foreground/90 font-default leading-relaxed">
            <p>
              I&apos;m Yassine Essaadi, and I run YE Consultancy as an independent technical SEO consultant. I work with UK businesses to diagnose and fix the technical factors that silently suppress organic visibility: broken crawl paths, indexation bloat, poor internal link architecture, JavaScript rendering issues, cascading redirect chains, and poorly structured sites that waste crawl budget on pages that shouldn&apos;t rank.
            </p>
            <p className="text-muted-foreground">
              I improve crawlability so search engines can efficiently access and understand your content, and I make sure the right pages appear in search results.
            </p>
          </div>

          {/* Consolidated Direct-Consultant Statement */}
          <div className="mt-8 p-5 sm:p-6 rounded-lg bg-card border border-border border-l-4 border-l-primary">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm sm:text-base text-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Direct consultant access:</span> Every audit, crawl analysis, and architectural recommendation is conducted personally by me, giving you direct access to senior technical expertise and straightforward, hands-on communication throughout.
              </p>
            </div>
          </div>

          {/* Action CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="rounded-md font-medium px-6 shadow-sm hover:shadow-md transition-all">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md font-medium px-6">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>

        {/* Structural Pillars Overview */}
        <div ref={detailsRef} className={`mt-16 md:mt-20 pt-12 border-t border-border transition-all duration-700 delay-150 ease-out ${detailsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coreFocusAreas.map((area, index) => {
            const Icon = area.icon;
            return <div key={area.title} data-index={index} className="p-6 rounded-lg bg-card border border-border flex flex-col justify-between">
                  <div>
                    <div className="size-10 rounded-md bg-secondary text-primary flex items-center justify-center mb-4">
                      <Icon className="size-5" />
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                      {area.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}